'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import PageHeader from '@/components/shared/PageHeader';
import SearchBar from '@/components/shared/SearchBar';
import FilterRow from '@/components/shared/FilterRow';
import EmptyState from '@/components/shared/EmptyState';
import { Skeleton } from '@/components/shared/Skeletons';
import { mockEvents, Event } from '@/data/mockEvents';
import { Calendar, MapPin, Users, Clock, Lock, ArrowRight } from 'lucide-react';

const categoryFilters = [
  { label: 'All', value: 'all' },
  { label: 'Fitness & Wellness', value: 'Fitness & Wellness' },
  { label: 'Social', value: 'Social' },
  { label: 'Arts & Culture', value: 'Arts & Culture' },
  { label: 'Pets', value: 'Pets' },
];

function EventCardSkeleton() {
  return (
    <div className="neon-card p-5">
      <div className="flex items-start gap-4 mb-4">
        <Skeleton className="w-14 h-14 rounded-lg flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-3 w-32" />
        </div>
      </div>
      <Skeleton className="h-3 w-full mb-1.5" />
      <Skeleton className="h-3 w-4/5 mb-4" />
      <div className="flex items-center justify-between">
        <Skeleton className="h-3 w-28" />
        <Skeleton className="h-5 w-16 rounded-full" />
      </div>
    </div>
  );
}

function EventDateBlock({ date }: { date: string }) {
  const parts = date.split(', ');
  const monthDay = parts[1] || '';
  const [month, day] = monthDay.split(' ');
  return (
    <div
      className="w-14 h-14 rounded-lg flex flex-col items-center justify-center flex-shrink-0"
      style={{
        background: 'rgba(76,201,255,0.08)',
        border: '1px solid rgba(76,201,255,0.18)',
      }}
    >
      <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#4CC9FF' }}>
        {month?.slice(0, 3)}
      </span>
      <span className="font-display font-bold text-[20px] leading-none" style={{ color: '#F2F4FF' }}>
        {day}
      </span>
    </div>
  );
}

function EventCard({ event }: { event: Event }) {
  const attendancePct = event.maxAttendees
    ? Math.round((event.attendeeCount / event.maxAttendees) * 100)
    : null;

  return (
    <Link href={`/events/${event.slug}`} className="block group focus-visible:outline-none">
      <div className="neon-card p-5 h-full group-focus-visible:border-[rgba(76,201,255,0.35)] group-focus-visible:shadow-[0_0_0_3px_rgba(76,201,255,0.15)]">
        <div className="flex items-start gap-4 mb-3">
          <EventDateBlock date={event.date} />
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-semibold text-[15px] leading-snug mb-1" style={{ color: '#F2F4FF' }}>
              {event.title}
            </h3>
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium"
              style={{
                background: 'rgba(122,92,255,0.10)',
                border: '1px solid rgba(122,92,255,0.20)',
                color: '#9B7FFF',
              }}
            >
              {event.category}
            </span>
          </div>
        </div>

        <p className="text-[13px] leading-relaxed mb-4 line-clamp-2" style={{ color: '#A7A3C4' }}>
          {event.description}
        </p>

        <div className="space-y-1.5 mb-4">
          <div className="flex items-center gap-2">
            <Clock size={12} style={{ color: '#4CC9FF', flexShrink: 0 }} />
            <span className="text-[12.5px]" style={{ color: 'rgba(242,244,255,0.65)' }}>
              {event.time}{event.endTime ? ` – ${event.endTime}` : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin size={12} style={{ color: '#4CC9FF', flexShrink: 0 }} />
            <span className="text-[12.5px] truncate" style={{ color: 'rgba(242,244,255,0.65)' }}>
              {event.location}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-1.5">
            <Users size={12} style={{ color: '#A7A3C4' }} />
            <span className="text-[12px]" style={{ color: '#A7A3C4' }}>
              {event.attendeeCount} attending
              {event.maxAttendees ? ` · ${event.maxAttendees - event.attendeeCount} spots left` : ''}
            </span>
          </div>
          {attendancePct !== null && attendancePct >= 80 && (
            <span
              className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
              style={{ background: 'rgba(240,168,50,0.12)', color: '#F0A832', border: '1px solid rgba(240,168,50,0.20)' }}
            >
              Filling up
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function EventsPage() {
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [isLoading] = useState(false);

  const filtered = mockEvents.filter((e) => {
    const matchesSearch =
      !search ||
      e.title.toLowerCase().includes(search.toLowerCase()) ||
      e.location.toLowerCase().includes(search.toLowerCase()) ||
      e.category.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = activeFilter === 'all' || e.category === activeFilter;
    return matchesSearch && matchesFilter;
  });

  // Mock: not approved — show teaser state
  const isApproved = false;

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="dark" />
      <main className="pt-16">
        <PageHeader
          eyebrow="Private Community"
          title="Events"
          description="Resident-only events across both towers. From morning yoga to evening socials — stay connected with what's happening."
          stat={{ value: String(mockEvents.length), label: 'upcoming events' }}
        />

        {/* Private launch teaser banner */}
        {!isApproved && (
          <div
            className="border-b"
            style={{ background: 'rgba(122,92,255,0.04)', borderColor: 'rgba(122,92,255,0.15)' }}
          >
            <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Lock size={14} style={{ color: '#9B7FFF', flexShrink: 0 }} />
                <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(167,163,196,0.80)' }}>
                  <span style={{ color: '#F2F4FF' }}>Community Events</span> is part of the private Panorama resident network. Access is rolling out in waves for verified residents.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Link href="/request-access" className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg transition-all duration-150" style={{ background: 'rgba(122,92,255,0.15)', border: '1px solid rgba(122,92,255,0.30)', color: '#C4BEFF' }}>
                  Request Access <ArrowRight size={11} />
                </Link>
                <Link href="/login" className="text-[12px] font-medium px-3 py-1.5 rounded-lg transition-all duration-150" style={{ color: 'rgba(167,163,196,0.60)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  Sign In
                </Link>
              </div>
            </div>
          </div>
        )}

        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-8">
          {/* Search + Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <SearchBar
                value={search}
                onChange={setSearch}
                placeholder="Search events, locations…"
              />
            </div>
          </div>
          <div className="mb-8">
            <FilterRow filters={categoryFilters} active={activeFilter} onChange={setActiveFilter} />
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => <EventCardSkeleton key={i} />)}
            </div>
          )}

          {/* Empty */}
          {!isLoading && filtered.length === 0 && (
            <EmptyState
              title="No events found"
              description="Try adjusting your search or filter to find upcoming community events."
              icon={<Calendar size={22} style={{ color: '#4CC9FF' }} />}
              action={
                <button
                  className="btn-secondary text-[13px] px-4 py-2"
                  onClick={() => { setSearch(''); setActiveFilter('all'); }}
                >
                  Clear filters
                </button>
              }
            />
          )}

          {/* Grid */}
          {!isLoading && filtered.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
